import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Hard-coded super-admin emails that always have full access.
 * This mirrors the server-side SUPER_ADMIN_EMAILS constant.
 * Must be kept in sync with server/src/config/super-admin.ts
 */
const SUPER_ADMIN_EMAILS = ["admin@admin.com"];

function isSuperAdminEmail(email?: string | null): boolean {
  if (!email) return false;
  const lower = email.toLowerCase().trim();
  return SUPER_ADMIN_EMAILS.some((e) => e.toLowerCase() === lower);
}

// Utility function to check if a user is a leader
export function isLeader(user: any): boolean {
  if (!user) return false;

  // Check if user has a leader role
  const roleName = user.role?.name || user.role;
  return roleName === "President" || roleName === "Accountant" || roleName === "Secretary";
}

// Utility function to check if a user is an admin
export function isAdmin(user: any): boolean {
  if (!user) return false;

  // Hard-coded super-admin emails always have full access
  if (isSuperAdminEmail(user?.email)) return true;

  // System users with Admin role are admins
  if (user.isAdmin === true) return true;

  // Check if user is a President, Secretary, or Accountant directly (even without isGroupLeader flag)
  const leadershipRole = user.leadershipRole || user.role?.name || user.role;
  const adminRoles = ["Technical Support", "President", "Secretary", "Accountant"];
  if (adminRoles.includes(leadershipRole)) return true;

  // Members are admins only if they are group leaders and have a leadership role.
  if (user.isGroupLeader === true) {
    const validLeadershipRoles = ["President", "Secretary", "Accountant", "Admin", "Technical Support"];
    return validLeadershipRoles.includes(leadershipRole);
  }

  return false;
}

// Utility function to check if a user can perform admin-like actions
export function canPerformAdminActions(user: any): boolean {
  if (!user) return false;

  // Use the isAdmin function
  return isAdmin(user);
}

/** Format a number as currency in FRW — safe against null/undefined/NaN */
export function formatCurrency(value: number | null | undefined): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0 FRW";
  return `${num.toLocaleString()} FRW`;
}

/** Safe money formatter — returns "0 FRW" for bad values */
export function formatMoney(value: number | null | undefined): string {
  return formatCurrency(value);
}

/** Safe number formatter — returns "0" for bad values */
export function formatNumber(value: number | null | undefined): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString();
}

/** Safe percent formatter — returns "0%" for bad values */
export function formatPercent(value: number | null | undefined): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0%";
  return `${num.toFixed(1)}%`;
}

/** Format a term in months to a human-readable string */
export function formatTerm(months: number): string {
  if (!months || months <= 0) return "";
  if (months >= 12) {
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return rem > 0 ? `${years} yr ${rem} mo` : `${years} ${years === 1 ? "Year" : "Years"}`;
  }
  return `${months} ${months === 1 ? "Month" : "Months"}`;
}

/** Full SQL backup contains sensitive data — only global/system administrators. */
export function canDownloadSystemBackup(user: any): boolean {
  if (!user) return false;

  // Hard-coded super-admin emails always have full access
  if (isSuperAdminEmail(user?.email)) return true;

  const roleName =
    typeof user.role === "string" ? user.role : user?.role?.name;
  const leadershipRole = user?.leadershipRole;
  return (
    user.isSystemUser === true ||
    user.isAdmin === true ||
    roleName === "Admin" ||
    user.role === "Admin" ||
    leadershipRole === "Admin" ||
    leadershipRole === "President" ||
    leadershipRole === "Secretary" ||
    leadershipRole === "Accountant"
  );
}
